import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  useAuth,
  type AuthFormError,
} from '@monecolier/ui';
import { getMe, getPrivacyPolicy, type LoginResponse } from '@monecolier/api-client';
import { AccessDenied } from '../components/AccessDenied';
import {
  INVITATION_CONTINUATION_STATE,
  acceptInvitationPath,
  readInvitationToken,
  withInvitationToken,
} from '../lib/invitationContinuation';

// Redirection après succès : "/" (Home) — pas de page protégée dédiée pour l'instant,
// voir AUT-01, extension frontend.
const REDIRECT_AFTER_LOGIN = '/';

export function Login() {
  // `apiClient` réutilisé tel quel depuis le contexte (pas importé séparément
  // depuis ../services/api) : c'est EXACTEMENT l'instance injectée dans
  // <AuthProvider> (réelle en prod, mockée en test) — voir le commentaire sur
  // AuthContextValue.apiClient.
  const { login, verifyMfa, acceptConsent, logout, apiClient } = useAuth();
  const navigate = useNavigate();
  // AUT-02 : message de confirmation transmis par ResetPassword.tsx via `state`
  // (jamais un paramètre d'URL — voir le commentaire équivalent dans
  // ResetPassword.tsx) après une réinitialisation de mot de passe réussie.
  const location = useLocation();
  const passwordResetConfirmation = (location.state as { passwordResetConfirmation?: string } | null)
    ?.passwordResetConfirmation;
  // AUT-32 : présent UNIQUEMENT quand on arrive depuis le bouton "Se connecter"
  // d'AcceptInvitation.tsx — absent, cette page reste inchangée.
  const [searchParams] = useSearchParams();
  const invitationToken = readInvitationToken(searchParams);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // AUT-05 : `mfaToken` non-null bascule le formulaire vers le second temps de la
  // connexion (saisie du code TOTP/de secours) — voir POST /auth/login,
  // apps/api/src/routes/auth.ts. `mfaCode` distinct de `password` : les deux étapes
  // ne partagent jamais leur champ de saisie.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  // CMP-02 : `consentToken` non-null bascule le formulaire vers l'écran de
  // re-sollicitation (compte existant, nouvelle version de la politique) — même
  // pattern que `mfaToken` ci-dessus, DEUXIÈME défi possible (après un MFA déjà
  // résolu le cas échéant, voir le commentaire de `resolveSessionOrConsentChallenge`
  // côté backend). Case JAMAIS pré-cochée, comme à l'inscription.
  const [consentToken, setConsentToken] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [policyText, setPolicyText] = useState<string | null>(null);
  const [policyExpanded, setPolicyExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AuthFormError | null>(null);
  // AUT-03 : des identifiants valides ne garantissent pas un accès au portail école —
  // il faut un Membership. On ne le sait qu'après login(), via GET /me.
  const [accessDenied, setAccessDenied] = useState(false);
  // AUT-25 : distingue "jamais eu de Membership" de "Membership révoquée" (voir
  // AccessDenied.tsx) — toujours posé EN MÊME TEMPS que `accessDenied` ci-dessus,
  // jamais déduit ailleurs.
  const [accessRevoked, setAccessRevoked] = useState(false);

  // CMP-02 : le défi de consentement transporte déjà `policyVersion` mais PAS le
  // texte lui-même (voir ConsentChallengeResponse) — chargé PARESSEUSEMENT ici,
  // seulement quand l'écran devient réellement nécessaire (pas à chaque montage de
  // /login, contrairement au formulaire d'inscription qui l'affiche toujours).
  function beginConsentChallenge(token: string) {
    setConsentToken(token);
    getPrivacyPolicy(apiClient)
      .then((policy) => setPolicyText(policy.text))
      .catch(() => {
        // Best-effort — voir le commentaire équivalent dans Register.tsx.
      });
  }

  // Commun aux DEUX chemins de connexion (sans MFA, et après verifyMfa réussi) —
  // AUT-05 : extrait pour ne pas dupliquer la décision accessDenied/navigate.
  async function completeLoginFlow(result: LoginResponse) {
    // AUT-32 : connexion depuis un lien d'invitation — retour direct sur
    // /accept-invitation (voir Register.tsx, même raisonnement) : un compte
    // existant SANS Membership (ex. employé réinvité après un départ) ne doit
    // pas voir `AccessDenied` avant que son invitation soit acceptée. Point
    // commun aux trois chemins (mot de passe, MFA, consentement), donc couvert
    // une seule fois ici.
    if (invitationToken) {
      navigate(acceptInvitationPath(invitationToken), { replace: true, state: INVITATION_CONTINUATION_STATE });
      return;
    }
    // `result.accessToken` (pas `useAuth().accessToken`, pas garanti à jour ici —
    // voir le commentaire sur AuthContextValue.login) passé explicitement à getMe().
    const me = await getMe(apiClient, result.accessToken);
    if (me.role === null) {
      setAccessRevoked(me.revoked);
      setAccessDenied(true);
    } else {
      navigate(REDIRECT_AFTER_LOGIN);
    }
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login({ email, password });
      // AUT-05 : mot de passe correct MAIS MFA activé sur ce compte — bascule vers
      // le second formulaire (code), JAMAIS d'appel à GET /me à ce stade (aucun
      // accessToken n'existe encore).
      if ('mfaRequired' in result) {
        setMfaToken(result.mfaToken);
        return;
      }
      // CMP-02 : mot de passe (et MFA, s'il n'était pas activé) déjà résolus, MAIS
      // le compte doit (ré-)accepter la politique — même bascule que ci-dessus,
      // AUCUN accessToken émis à ce stade non plus.
      if ('consentRequired' in result) {
        beginConsentChallenge(result.consentToken);
        return;
      }
      await completeLoginFlow(result);
    } catch (err) {
      // describeAuthFormError() journalise TOUJOURS l'erreur réelle via
      // console.error avant de la traduire — voir packages/ui/src/auth/
      // authFormError.ts pour le bug réel (CORS silencieux) que cette
      // journalisation permanente permet désormais de diagnostiquer.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyMfa({ mfaToken: mfaToken!, code: mfaCode });
      // Le MFA est RÉSOLU à ce stade, quel que soit ce qui suit — `mfaToken` doit
      // être effacé pour que l'écran MFA (`mfaToken !== null`, vérifié EN PREMIER
      // ci-dessous) n'écrase jamais l'écran suivant (défi de consentement, ou Home
      // directement) : bug réel trouvé en écrivant le test CMP-02 "MFA + consentement"
      // (l'écran MFA restait affiché indéfiniment, `mfaToken` n'était auparavant
      // jamais réinitialisé car ce cas ne s'était encore jamais produit — la
      // navigation vers Home démontait le composant avant que ça ne compte).
      setMfaToken(null);
      // CMP-02 : le code MFA vient d'être validé, MAIS le compte doit encore
      // accepter la politique — même bascule qu'après un mot de passe seul, voir
      // handlePasswordSubmit ci-dessus.
      if ('consentRequired' in result) {
        beginConsentChallenge(result.consentToken);
        return;
      }
      await completeLoginFlow(result);
    } catch (err) {
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConsentSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await acceptConsent({ consentToken: consentToken! });
      await completeLoginFlow(result);
    } catch (err) {
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (accessDenied) {
    return (
      <AccessDenied
        revoked={accessRevoked}
        onLogout={() => {
          logout();
          setAccessDenied(false);
          setAccessRevoked(false);
        }}
      />
    );
  }

  if (mfaToken !== null) {
    return (
      <Card title="Vérification en deux étapes">
        <p className={secondaryTextClassName}>
          Saisissez le code affiché par votre application d&apos;authentification, ou l&apos;un de vos codes de
          secours.
        </p>
        <form onSubmit={handleMfaSubmit} className="flex flex-col gap-4">
          <label className={formFieldClassName}>
            Code de vérification
            <input
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(event) => setMfaCode(event.target.value)}
              required
              autoFocus
              className={formInputClassName}
            />
          </label>
          {error ? (
            <p role="alert" data-testid="login-mfa-error" className={formErrorClassName(error.kind)}>
              {error.message}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Vérification...' : 'Vérifier'}
          </Button>
        </form>
      </Card>
    );
  }

  if (consentToken !== null) {
    return (
      <Card title="Politique de confidentialité mise à jour">
        <p className={secondaryTextClassName}>
          La politique de confidentialité a été mise à jour depuis votre dernière connexion. Merci de la relire et
          de l&rsquo;accepter pour continuer.
        </p>
        <form onSubmit={handleConsentSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(event) => setConsentAccepted(event.target.checked)}
                data-testid="login-consent-checkbox"
              />
              <span>
                J&rsquo;accepte la{' '}
                <button
                  type="button"
                  onClick={() => setPolicyExpanded((expanded) => !expanded)}
                  className={linkClassName}
                  data-testid="login-consent-toggle"
                >
                  politique de confidentialité
                </button>
                .
              </span>
            </label>
            {policyExpanded ? (
              <div
                className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
                data-testid="login-consent-policy-text"
              >
                {policyText ?? 'Chargement…'}
              </div>
            ) : null}
          </div>
          {error ? (
            <p role="alert" data-testid="login-consent-error" className={formErrorClassName(error.kind)}>
              {error.message}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting || !consentAccepted}>
            {submitting ? 'Envoi...' : 'Continuer'}
          </Button>
        </form>
      </Card>
    );
  }

  return (
    <Card title="Connexion — Portail École">
      {passwordResetConfirmation ? (
        <p role="status" data-testid="login-password-reset-confirmation" className="text-sm text-brand-700">
          {passwordResetConfirmation}
        </p>
      ) : null}
      <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          Mot de passe
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
            className={formInputClassName}
          />
        </label>
        {error ? (
          <p role="alert" data-testid="login-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Connexion...' : 'Se connecter'}
        </Button>
      </form>
      <p className={secondaryTextClassName}>
        <Link to="/forgot-password" className={linkClassName}>
          Mot de passe oublié ?
        </Link>
      </p>
      <p className={secondaryTextClassName}>
        Pas encore de compte ? <Link to={withInvitationToken('/register', invitationToken)} className={linkClassName}>Créer un compte</Link>
      </p>
    </Card>
  );
}
